import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap, throwError } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})

export class GeneralService {

    private licenseSettingsSubject = new BehaviorSubject<any>(null);
    licenseSettings$ = this.licenseSettingsSubject.asObservable();
    private sameEmailVendors$: Observable<any[]>;

    // Read live rather than captured at construction: on native, this service is
    // constructed once at app boot (AppComponent injects it eagerly), but
    // environment.apiBaseUrl can change afterwards — e.g. right after onboarding
    // registers a market in the same running session, with no app restart. A
    // cached field would keep pointing at whatever URL (or none) was active when
    // this singleton was first created.
    private get baseURL(): string {
        return environment.apiBaseUrl;
    }
    constructor(private http: HttpClient) {}

    getReportUrl(marketId: number): Observable<string> {
        return this.http.get(
            `${this.baseURL}/General/GetReportUrl`,
            { responseType: 'text' }
        ).pipe(
            catchError(this.handleError)
        );
    }

    getWebInquiryVendorsWithSameEmail(vendorId: number, marketId: number): Observable<any[]> {
        if (this.sameEmailVendors$ === null || this.sameEmailVendors$ === undefined) {
            this.sameEmailVendors$ = this.http.get<any[]>(
                `${this.baseURL}/General/GetWebInquiryVendorsWithSameEmail`,
                {
                    params: {
                        vendorId: vendorId.toString(),
                        marketId: marketId.toString()
                    }
                }
            ).pipe(
                shareReplay(1),
                catchError(this.handleError)
            );
        }

        return this.sameEmailVendors$;
    }

    clearVendorCache(): void {
        this.sameEmailVendors$ = null;
    }

    getAllProcessedVendorPayouts(marketId: number): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetAllProcessedVendorPayouts?marketId=${marketId}`,
            { observe: 'response' }
        ).pipe(
            map(res => res.body as any),
            catchError(this.handleError)
        );
    }

    getWebInquiryUser(vendorId: number, marketId: number): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetWebInquiryUser`,
            {
                params: {
                    vendorId: vendorId.toString(),
                    marketId: marketId.toString()
                }
            }
        ).pipe(
            catchError(this.handleError)
        );
    }

    getTermsUsedInMarket(marketId): Observable<any> {
        return this.http.get(
            `${this.baseURL}/General/GetTermsUsedInMarket?marketId=${marketId}`,
            { observe: 'response' }
        ).pipe(
            map((res: HttpResponse<any>) => res.body),
            catchError(this.handleError)
        );
    }

    getMarketCurrentTime(marketId: number): Observable<string> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetMarketCurrentTime?marketId=${marketId}`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    return res.body as string;
                }),
                catchError(this.handleError)
            );
    }

    getASCloudLicenseSettings(marketId: number): Observable<any> {
        // Already loaded → return cached value
        if (this.licenseSettingsSubject.value) {
            return of(this.licenseSettingsSubject.value);
        }

        return this.http.get<any>(`${this.baseURL}/General/ASCloudLicenseSettings?marketId=${marketId}`)
            .pipe(
                tap(res => {
                    this.licenseSettingsSubject.next(res);
                })
            );
    }

    getNewSales(vendorId: number, marketId: number): Observable<any[]> {
        return this.http.get<any[]>(
            `${this.baseURL}/General/GetNewSales`,
            {
                params: {
                    vendorId: vendorId.toString(),
                    marketId: marketId.toString()
                }
            }
        ).pipe(
            catchError(this.handleError)
        );
    }

    // ---- NEW: server-side record of dismissed notifications (survives reinstall) ----
    // BACKEND TODO: add these two endpoints (table: VendorId, MarketId, NotificationKey).
    getDismissedNotifications(vendorId: number, marketId: number): Observable<string[]> {
        return this.http.get<string[]>(
            `${this.baseURL}/General/GetDismissedNotifications`,
            {
                params: {
                    vendorId: vendorId.toString(),
                    marketId: marketId.toString()
                }
            }
        ).pipe(
            catchError(this.handleError)
        );
    }

    dismissNotifications(vendorId: number, marketId: number, ids: string[]): Observable<any> {
        return this.http.post<any>(
            `${this.baseURL}/General/DismissNotifications`,
            { VendorId: vendorId, MarketId: marketId, Ids: ids }
        ).pipe(
            catchError(this.handleError)
        );
    }

    get isInventoryEnabled(): boolean {
        return this.licenseSettingsSubject.value?.EnableInventoryControl ?? false;
    }

    getFlatCommission(marketId: number, vendorId: number): Observable<number> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetFlatCommission`,
            {
                params: {
                    marketId: marketId.toString(),
                    vendorId: vendorId.toString()
                }
            }
        ).pipe(
            catchError(this.handleError)
        );
    }

    getCustomMarketSettings(marketId: number): Observable<any> {
        return this.http.get<any>
            (`${this.baseURL}/General/GetCustomMarketSettings?marketId=${marketId}`,
                { observe: 'response' }
            ).pipe(
                map((res: HttpResponse<any>) => res.body),
                catchError(this.handleError)
            );
    }

    getPosSettings(marketId: number): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/General/GetPosSettings?marketId=${marketId}`,
            { observe: 'response' }
        ).pipe(
            map((res: HttpResponse<any>) => res.body),
            catchError(this.handleError)
        );
    }

    private handleError(error: HttpErrorResponse) {
        console.error('server error:', error);
        if (error.error instanceof Error) {
            const errMessage = error.error.message;
            return throwError(errMessage);
        }
        return throwError(error || 'Node.js server error');
    }

    
}